/**
 * Sellox POS Offline Data Cache
 * Uses IndexedDB for large data (products, customers, pending orders)
 * Falls back to localStorage if IndexedDB unavailable.
 */

import { idbSave, idbLoad, idbGetMeta, STORES, timeAgo, clearAllDataStores } from './offlineDB';

const LS_PREFIX = 'pos_cache_';
const LS_META_KEY = 'pos_cache_meta';

// Cache keys used by POS
export const CACHE_KEYS = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  CUSTOMERS: 'customers',
  EXCHANGE_RATES: 'exchange_rates',
  COMBOS: 'combos',
  SETTINGS: 'settings',
  WAREHOUSES: 'warehouses',
  STORES: 'stores',
  HELD_ORDERS: 'held_orders',
};

// Map cache keys to IndexedDB stores (large data) or null (use localStorage)
const IDB_MAP = {
  products: STORES.PRODUCTS,
  categories: STORES.CATEGORIES,
  customers: STORES.CUSTOMERS,
  combos: STORES.COMBOS,
};

// Save data to cache (IndexedDB for large data, localStorage for small)
export async function cacheData(key, data) {
  const idbStore = IDB_MAP[key];
  if (idbStore) {
    await idbSave(idbStore, key, data);
  }
  // Always also save to localStorage as fallback
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(data));
    const meta = JSON.parse(localStorage.getItem(LS_META_KEY) || '{}');
    meta[key] = { updated: new Date().toISOString(), count: Array.isArray(data) ? data.length : 1 };
    localStorage.setItem(LS_META_KEY, JSON.stringify(meta));
  } catch (e) {
    if (e.name === 'QuotaExceededError') {
      // localStorage full - clear old small caches, keep IndexedDB
      ['exchange_rates', 'settings', 'warehouses', 'stores'].forEach(k => {
        try { localStorage.removeItem(LS_PREFIX + k); } catch {}
      });
      try { localStorage.setItem(LS_PREFIX + key, JSON.stringify(data)); } catch {}
    }
  }
}

// Load data from cache (try IndexedDB first, then localStorage)
export async function loadCachedData(key, fallback = null) {
  const idbStore = IDB_MAP[key];
  if (idbStore) {
    const data = await idbLoad(idbStore, key, null);
    if (data) return data;
  }
  // Fallback to localStorage
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (raw) return JSON.parse(raw);
  } catch {}
  return fallback;
}

// Get cache metadata
export function getCacheMeta() {
  try { return JSON.parse(localStorage.getItem(LS_META_KEY) || '{}'); }
  catch { return {}; }
}

// Get human-readable cache age for a key
export function getCacheAge(key) {
  const meta = getCacheMeta();
  if (!meta[key]?.updated) return null;
  return timeAgo(meta[key].updated);
}

// Check if cache is fresh enough
export function isCacheFresh(key, maxAgeMs = 24 * 60 * 60 * 1000) {
  const meta = getCacheMeta();
  if (!meta[key]?.updated) return false;
  return (Date.now() - new Date(meta[key].updated).getTime()) < maxAgeMs;
}

// Get staleness level: 'fresh' (<1h), 'stale' (1-24h), 'old' (>24h), 'none'
export function getStalenessLevel(key) {
  const meta = getCacheMeta();
  if (!meta[key]?.updated) return 'none';
  const age = Date.now() - new Date(meta[key].updated).getTime();
  if (age < 60 * 60 * 1000) return 'fresh'; // <1 hour
  if (age < 24 * 60 * 60 * 1000) return 'stale'; // 1-24 hours
  return 'old'; // >24 hours
}

// Clear all caches
export function clearAllCaches() {
  Object.keys(localStorage).filter(k => k.startsWith(LS_PREFIX)).forEach(k => localStorage.removeItem(k));
  localStorage.removeItem(LS_META_KEY);
}

// Force clear ALL data caches (IndexedDB + localStorage), returns promise
export async function forceWipeAllCaches() {
  // 1. Clear localStorage caches
  clearAllCaches();
  // 2. Clear IndexedDB data stores (keeps pending_orders)
  await clearAllDataStores();
  // 3. Clear Service Worker caches (static assets)
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(names.map(n => caches.delete(n)));
  }
  return true;
}

// App version for cache busting - bump this when deploying
export const APP_CACHE_VERSION = 'v11';

// Check if app version changed, if so wipe data caches
export async function checkVersionAndClearCache() {
  const storedVersion = localStorage.getItem('sellox_app_version');
  if (storedVersion && storedVersion !== APP_CACHE_VERSION) {
    console.log(`App version changed: ${storedVersion} -> ${APP_CACHE_VERSION}, clearing caches`);
    await clearAllDataStores();
    clearAllCaches();
  }
  localStorage.setItem('sellox_app_version', APP_CACHE_VERSION);
}
